import { useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Wrench, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useDeskServiceRequest, type DeskLocation } from '@/hooks/usePhoneDesk';
import { SERVICE_KINDS } from './deskFormat';

/**
 * The phone desk's "Service request" form. Goes through desk-service-request →
 * fileServiceCall(), the same path the portal chat and the voice agent use:
 * the request is saved, pushed into Service Fusion and the service team is
 * emailed. Tagged source='phone_desk' with the receptionist's name.
 */
export function DeskServicePanel({
  customerId,
  locations,
  initialLocationId,
  onClose,
}: {
  customerId: string;
  locations: DeskLocation[];
  initialLocationId: string | null;
  onClose: () => void;
}) {
  const file = useDeskServiceRequest();
  const [locationId, setLocationId] = useState(initialLocationId ?? '');
  const [kind, setKind] = useState<(typeof SERVICE_KINDS)[number][0]>('equipment_issue');
  const [subject, setSubject] = useState('');
  const [details, setDetails] = useState('');
  const [callerName, setCallerName] = useState('');
  const [callback, setCallback] = useState('');
  const [done, setDone] = useState<{ request_id: string; sf_job_id: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const res = await file.mutateAsync({
        customer_id: customerId,
        location_id: locationId || undefined,
        kind,
        subject: subject.trim(),
        details: details.trim() || undefined,
        caller_name: callerName.trim() || undefined,
        callback_phone: callback.trim() || undefined,
      });
      setDone(res);
    } catch (err) {
      setError((err as Error).message || 'Could not open the request.');
    }
  }

  return (
    <div className="rounded-xl border-2 border-brix-primary/40 bg-brix-surface shadow-card-hover" role="region" aria-label="Service request">
      <div className="flex items-center gap-2 border-b border-brix-border px-4 py-3">
        <Wrench size={16} className="text-brix-primary" aria-hidden />
        <h2 className="font-display text-base font-bold text-brix-ink">Service request</h2>
        <button type="button" onClick={onClose} className="ml-auto rounded p-1 text-brix-muted hover:bg-brix-surface-alt hover:text-brix-ink" aria-label="Close (Esc)">
          <X size={16} />
        </button>
      </div>
      {done ? (
        <div className="px-4 py-8 text-center">
          <CheckCircle2 size={36} className="mx-auto text-brix-success" aria-hidden />
          <p className="mt-3 font-display text-lg font-bold text-brix-ink">Service request opened</p>
          {done.sf_job_id ? (
            <>
              <p className="mt-1 text-sm text-brix-muted">Read the ticket number to the caller:</p>
              <p className="mt-2 font-mono text-2xl font-bold tracking-wide text-brix-primary">{done.sf_job_id}</p>
            </>
          ) : (
            <p className="mt-1 text-sm text-brix-muted">The service team has it.</p>
          )}
          <p className="mx-auto mt-3 max-w-md text-sm text-brix-muted">Tell the caller the service team will call them to schedule the visit.</p>
          <Button className="mt-5" onClick={onClose}>Done</Button>
        </div>
      ) : (
        <form onSubmit={(e) => void submit(e)} className="grid gap-3 p-4 sm:grid-cols-2">
          {locations.length > 1 && (
            <label className="block text-xs font-semibold text-brix-muted sm:col-span-2">
              Location
              <select
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                className="mt-1 h-10 w-full rounded-lg border border-brix-border bg-brix-surface px-3 text-sm font-normal text-brix-ink focus:border-brix-primary focus:outline-none"
              >
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>{l.name}{l.address ? ` — ${l.address}` : ''}</option>
                ))}
              </select>
            </label>
          )}
          <fieldset className="sm:col-span-2">
            <legend className="text-xs font-semibold text-brix-muted">What is it about?</legend>
            <div className="mt-1 flex flex-wrap gap-2">
              {SERVICE_KINDS.map(([k, label]) => (
                <label
                  key={k}
                  className={`cursor-pointer rounded-lg border px-3 py-1.5 text-sm ${kind === k ? 'border-brix-primary bg-brix-primary-tint font-semibold text-brix-primary' : 'border-brix-border text-brix-ink hover:bg-brix-surface-alt'}`}
                >
                  <input type="radio" name="kind" value={k} checked={kind === k} onChange={() => setKind(k)} className="sr-only" />
                  {label}
                </label>
              ))}
            </div>
          </fieldset>
          <label className="block text-xs font-semibold text-brix-muted sm:col-span-2">
            One-line summary *
            <Input autoFocus required maxLength={200} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Soda gun not pouring Coke" className="mt-1" />
          </label>
          <label className="block text-xs font-semibold text-brix-muted sm:col-span-2">
            Details
            <textarea
              value={details}
              maxLength={4000}
              onChange={(e) => setDetails(e.target.value)}
              rows={3}
              placeholder="What they said, which machine, when it started"
              className="mt-1 w-full rounded-lg border border-brix-border bg-brix-surface px-3 py-2 text-sm font-normal text-brix-ink focus:border-brix-primary focus:outline-none"
            />
          </label>
          <label className="block text-xs font-semibold text-brix-muted">
            Caller's name
            <Input value={callerName} maxLength={120} onChange={(e) => setCallerName(e.target.value)} className="mt-1" />
          </label>
          <label className="block text-xs font-semibold text-brix-muted">
            Callback number
            <Input type="tel" value={callback} maxLength={40} onChange={(e) => setCallback(e.target.value)} className="mt-1" />
          </label>
          {error && (
            <p role="alert" className="flex items-start gap-1.5 text-sm text-brix-ink sm:col-span-2">
              <AlertTriangle size={14} className="mt-0.5 shrink-0 text-brix-warning" aria-hidden /> {error}
            </p>
          )}
          <div className="flex justify-end gap-2 sm:col-span-2">
            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={!subject.trim() || file.isPending}>
              {file.isPending ? <Loader2 size={14} className="animate-spin" /> : <Wrench size={14} aria-hidden />}
              Open service request
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
